import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPaperPlane,
  faBan,
  faExclamationTriangle,
  faSpinner,
  faEnvelope,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import api from '../axiosInstance';

const REJECTION_STAGES = [
  'Resume Screening',
  'Phone Screening',
  '1st Technical Round',
  '2nd Technical Round',
  'Managerial Round',
  'HR Round',
  'Technical Assessment',
  'On-site Interview',
  'Reference Check',
  'Other / General',
];

function RejectionModal({ show, candidate, onClose, onSuccess, isQualified = false }) {
  const [rejectionStage, setRejectionStage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (candidate) {
      const currentStage = candidate.interviewStatus && candidate.interviewStatus !== 'Not Started' && candidate.interviewStatus !== 'Rejected'
        ? candidate.interviewStatus
        : '1st Technical Round';
      
      setRejectionStage(currentStage);
      setRejectionReason('');
      setSendEmail(true);

      const role = candidate.roleName || 'the position';
      setEmailSubject(`Update regarding your application for ${role} at CoreHire`);
      
      setEmailBody(
        `<p>Dear ${candidate.name || 'Candidate'},</p>` +
        `<p>Thank you for taking the time to interview with us for the <strong>${role}</strong> role.</p>` +
        `<p>After careful consideration following the <strong>${currentStage}</strong>, we regret to inform you that we will not be advancing your application for this position.</p>` +
        `<p>We sincerely appreciate your interest in joining our team and the time you spent during our selection process. We wish you every success in your future endeavors.</p>` +
        `<p>Best regards,<br/><strong>Hiring Team</strong></p>`
      );
    }
  }, [candidate]);

  useEffect(() => {
    if (candidate && rejectionStage) {
      const role = candidate.roleName || 'the position';
      setEmailBody(
        `<p>Dear ${candidate.name || 'Candidate'},</p>` +
        `<p>Thank you for taking the time to interview with us for the <strong>${role}</strong> role.</p>` +
        `<p>After careful consideration following the <strong>${rejectionStage}</strong>, we regret to inform you that we will not be advancing your application for this position.</p>` +
        `<p>We sincerely appreciate your interest in joining our team and the time you spent during our selection process. We wish you every success in your future endeavors.</p>` +
        `<p>Best regards,<br/><strong>Hiring Team</strong></p>`
      );
    }
  }, [rejectionStage, candidate]);

  if (!show || !candidate) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const endpoint = isQualified
        ? `/qualified-candidates/${candidate._id}/reject`
        : `/candidates/${candidate._id}/reject`;

      const payload = {
        rejectionStage,
        rejectionReason,
        sendEmail,
        emailSubject,
        emailBody,
      };

      const response = await api.post(endpoint, payload);
      setSubmitting(false);

      if (onSuccess) {
        onSuccess(response.data.candidate || response.data);
      }
      onClose();
    } catch (err) {
      console.error('Error rejecting candidate:', err);
      setSubmitting(false);
      setError(
        err.response?.data?.message || err.message || 'Failed to reject candidate. Please try again.'
      );
    }
  };

  return (
    <div className="coreops-modal-backdrop" style={modalStyles.backdrop}>
      <div className="coreops-modal-container" style={modalStyles.container}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>
            <FontAwesomeIcon icon={faBan} style={{ color: '#d9534f', marginRight: '8px' }} />
            Reject Candidate - {candidate.name}
          </h3>
          <button onClick={onClose} style={modalStyles.closeButton} title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={modalStyles.body}>
          {error && (
            <div style={modalStyles.errorAlert}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
              {error}
            </div>
          )}

          <div style={modalStyles.infoBanner}>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px', color: '#0077b6' }} />
            Current Status: <strong>{candidate.interviewStatus || 'Not Started'}</strong> | Candidate Email: <strong>{candidate.email}</strong>
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>
              Stage of Rejection <span style={{ color: '#d9534f' }}>*</span>
            </label>
            <select
              value={rejectionStage}
              onChange={(e) => setRejectionStage(e.target.value)}
              style={modalStyles.select}
              required
            >
              {REJECTION_STAGES.map((stg) => (
                <option key={stg} value={stg}>
                  {stg}
                </option>
              ))}
            </select>
          </div>

          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Internal Rejection Reason / Notes (Optional)</label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter specific reasons or feedback for internal records..."
              rows={2}
              style={modalStyles.textarea}
            />
          </div>

          <div style={modalStyles.checkboxGroup}>
            <label style={modalStyles.checkboxLabel}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: '#0077b6' }} />
              Send Rejection Email to Candidate ({candidate.email})
            </label>
          </div>

          {sendEmail && (
            <div style={modalStyles.emailSection}>
              <h4 style={modalStyles.emailSectionTitle}>Custom Rejection Email Preview</h4>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Email Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  style={modalStyles.input}
                  required={sendEmail}
                />
              </div>

              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Email Content (HTML)</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  style={modalStyles.textareaCode}
                  required={sendEmail}
                />
              </div>
            </div>
          )}

          <div style={modalStyles.footer}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={modalStyles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={modalStyles.rejectButton}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                  Processing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={sendEmail ? faPaperPlane : faBan} style={{ marginRight: '6px' }} />
                  {sendEmail ? 'Reject & Send Email' : 'Reject Candidate'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const modalStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(4px)',
  },
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '650px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
    border: '1px solid #e0e0e0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #eaeaea',
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#212529',
    fontWeight: '600',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#6c757d',
    cursor: 'pointer',
  },
  body: {
    padding: '24px',
  },
  errorAlert: {
    backgroundColor: '#f8d7da',
    color: '#842029',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '14px',
    border: '1px solid #f5c2c7',
  },
  infoBanner: {
    backgroundColor: '#e7f5ff',
    color: '#004085',
    padding: '10px 14px',
    borderRadius: '6px',
    marginBottom: '18px',
    fontSize: '13px',
    border: '1px solid #bbeeef',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '6px',
    color: '#333333',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #cccccc',
    fontSize: '14px',
    backgroundColor: '#ffffff',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #cccccc',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  textareaCode: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #cccccc',
    fontSize: '13px',
    fontFamily: 'monospace',
    backgroundColor: '#fafafa',
    resize: 'vertical',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #cccccc',
    fontSize: '14px',
  },
  checkboxGroup: {
    marginBottom: '18px',
    padding: '8px 0',
  },
  checkboxLabel: {
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: '#212529',
  },
  emailSection: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
  },
  emailSectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: '#374151',
    fontWeight: '600',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #eaeaea',
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #6c757d',
    backgroundColor: '#ffffff',
    color: '#6c757d',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  rejectButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#d9534f',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(217, 83, 79, 0.4)',
  },
};

export default RejectionModal;
